import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Divider,
  Link,
} from '@mui/material';

const Section = ({ id, title, children }) => (
  <Box id={id} sx={{ mb: 4 }}>
    <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: 'text.primary' }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const TermsAndConditions = () => {
  return (
    <>
      {/* Header */}
      <Box sx={{ bgcolor: 'background.paper', pt: { xs: 8, md: 10 }, pb: 4, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Container maxWidth="md">
          <Typography component="h1" variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
            Terms and Conditions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Last Updated: 01 August 2026
          </Typography>
        </Container>
      </Box>

      {/* Body */}
      <Container maxWidth="md" sx={{ py: { xs: 5, md: 7 } }}>
        {/* Intro */}
        <Typography variant="body1" paragraph>
          These Terms and Conditions ("<strong>Terms</strong>") constitute a legally binding agreement between <strong>Creativecrewz</strong>, having its registered office at <strong>6-3-240/6/1, 1st Floor, Sarada Street, Road D-block, Maheswari Towers, Prem Nagar Road adj. Lane, Banjara Hills, Hyderabad, Telangana 500004, India</strong> ("<strong>Company</strong>", "<strong>we</strong>", "<strong>us</strong>", "<strong>our</strong>"), and any person accessing or using <strong>InfHire (www.infhire.com)</strong> (the "<strong>Platform</strong>"), whether as a brand/advertiser ("<strong>Brand</strong>"), a content creator/influencer ("<strong>Influencer</strong>"), or a visitor (collectively, "<strong>User</strong>", "<strong>you</strong>").
        </Typography>
        <Typography variant="body1" paragraph>
          These Terms are published in accordance with the Information Technology Act, 2000, the Information Technology (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021, the Consumer Protection Act, 2019, the Consumer Protection (E-Commerce) Rules, 2020, and the Indian Contract Act, 1872.
        </Typography>
        <Typography variant="body1" paragraph>
          By registering, accessing, or using the Platform, you agree to be bound by these Terms and our{' '}
          <Link component={RouterLink} to="/privacy" color="primary">Privacy Policy</Link>.
          {' '}If you do not agree, you must not use the Platform.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Section id="eligibility" title="1. Eligibility">
          <Typography variant="body1" component="div" sx={{ lineHeight: 2 }}>
            1.1 You must be at least <strong>18 years of age</strong> and competent to contract under the Indian Contract Act, 1872, to use the Platform.<br />
            1.2 If registering on behalf of an entity, you represent that you have the authority to bind that entity to these Terms.<br />
            1.3 We reserve the right to refuse registration or terminate access at our sole discretion if eligibility criteria are not met.
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="nature" title="2. Nature of the Platform">
          <Typography variant="body1" component="div" sx={{ lineHeight: 2 }}>
            2.1 The Platform is an intermediary that facilitates connections between Brands and Influencers for marketing collaborations. We act solely as a facilitator and are <strong>not a party</strong> to any collaboration, contract, or transaction agreed between a Brand and an Influencer, unless expressly stated otherwise.<br />
            2.2 We do not guarantee the outcome, quality, performance, or results of any campaign, content, or collaboration.
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="account-registration" title="3. Account Registration">
          <Typography variant="body1" component="div" sx={{ lineHeight: 2 }}>
            3.1 You must provide accurate, current, and complete information during registration and keep it updated.<br />
            3.2 You are responsible for maintaining the confidentiality of your login credentials and for all activities under your account.<br />
            3.3 You must notify us immediately at{' '}
            <Link href="mailto:contact@infhire.com" color="primary">contact@infhire.com</Link>
            {' '}of any unauthorized use of your account.
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="roles" title="4. Roles and Responsibilities">
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>4.1 Influencers</Typography>
          <Typography variant="body1" component="ul" sx={{ pl: 2, mb: 2 }}>
            <li>Must disclose accurate follower counts, engagement metrics, and audience demographics.</li>
            <li>Must not use fake followers, bots, or engagement pods to misrepresent reach.</li>
            <li>Must deliver content as per agreed campaign briefs, timelines, and usage rights.</li>
            <li>Must comply with the <strong>ASCI (Advertising Standards Council of India) Guidelines for Influencer Advertising in Digital Media</strong>, including clear and prominent disclosure of material connections using labels such as <strong>"#Ad", "#Sponsored", "#Collab", or "Paid Partnership"</strong> on every piece of sponsored content.</li>
          </Typography>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>4.2 Brands</Typography>
          <Typography variant="body1" component="ul" sx={{ pl: 2, mb: 2 }}>
            <li>Must provide accurate campaign briefs, product information, and claims for content to be created.</li>
            <li>Are solely responsible for the accuracy of product/service claims communicated to Influencers under the <strong>Consumer Protection Act, 2019</strong> and the <strong>Central Consumer Protection Authority (CCPA) Guidelines on Misleading Advertisements</strong>.</li>
            <li>Must make timely payments to Influencers as per agreed campaign terms.</li>
          </Typography>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>4.3 Both Parties</Typography>
          <Typography variant="body1" component="ul" sx={{ pl: 2 }}>
            <li>Must not engage in circumvention of the Platform to avoid applicable fees, once a collaboration is initiated through the Platform (<strong>No Circumvention Clause</strong>), for a period of <strong>12 months</strong> from introduction.</li>
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="fees" title="5. Fees, Payments, and Taxes">
          <Typography variant="body1" component="div" sx={{ lineHeight: 2 }}>
            5.1 Applicable service/platform fees, commission structures, and payment schedules will be as displayed on the Platform or agreed in a separate commercial agreement.<br />
            5.2 All payments are subject to applicable <strong>GST</strong>, <strong>TDS</strong>, and other statutory deductions under Indian tax law. Users are responsible for their own tax compliance, including GST registration where applicable.<br />
            5.3 We may use third-party payment gateways; we are not liable for delays or failures caused by such third parties.<br />
            5.4 Refunds, if any, shall be governed by our{' '}
            <Link component={RouterLink} to="/refund-policy" color="primary">Refund and Cancellation Policy</Link>,
            {' '}where applicable.
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="content" title="6. Content Ownership and License">
          <Typography variant="body1" component="div" sx={{ lineHeight: 2 }}>
            6.1 Influencers retain ownership of original content created, subject to the usage rights granted to Brands as specified in the applicable campaign brief or agreement (e.g., organic-only, paid boosting, whitelisting, usage duration).<br />
            6.2 By uploading content to the Platform, Users grant the Company a limited, non-exclusive, royalty-free, worldwide license to display, reproduce, and use such content solely for Platform operation, portfolio display, and promotional purposes, unless the User opts out in writing.<br />
            6.3 Users represent that content submitted does not infringe any third-party <strong>copyright, trademark, or personality rights</strong>, and shall indemnify the Company against any claims arising from such infringement.
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="advertising-compliance" title="7. Mandatory Disclosure and Advertising Compliance">
          <Typography variant="body1" paragraph>
            7.1 All sponsored content published by Influencers through campaigns facilitated on the Platform must comply with:
          </Typography>
          <Typography variant="body1" component="ul" sx={{ pl: 2 }}>
            <li>ASCI's Guidelines for Influencer Advertising in Digital Media</li>
            <li>Consumer Protection Act, 2019 and CCPA Guidelines on Misleading Advertisements, 2022</li>
            <li>Applicable platform-specific disclosure tools (e.g., Instagram's "Paid Partnership" tag)</li>
          </Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            7.2 Failure to comply with disclosure requirements may result in suspension or termination of the User's account, without prejudice to any regulatory action against the User directly.
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="prohibited-conduct" title="8. Prohibited Conduct">
          <Typography variant="body1" paragraph>Users shall not:</Typography>
          <Typography variant="body1" component="ul" sx={{ pl: 2 }}>
            <li>Post false, defamatory, obscene, or unlawful content</li>
            <li>Infringe any intellectual property or publicity rights</li>
            <li>Use the Platform for money laundering, fraud, or circumvention of tax obligations</li>
            <li>Misrepresent identity, credentials, or performance metrics</li>
            <li>Upload viruses, malware, or attempt unauthorized access to the Platform</li>
            <li>Violate the <strong>Information Technology Act, 2000</strong>, or any other applicable law</li>
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="intellectual-property" title="9. Intellectual Property">
          <Typography variant="body1" component="div" sx={{ lineHeight: 2 }}>
            9.1 The Platform, including its name, logo, design, software, and content (excluding User-generated content), is the exclusive property of the Company and protected under the <strong>Copyright Act, 1957</strong> and <strong>Trade Marks Act, 1999</strong>.<br />
            9.2 No User shall copy, modify, or reverse-engineer any part of the Platform without prior written consent.
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="disclaimer" title="10. Disclaimer of Warranties">
          <Typography variant="body1" component="div" sx={{ lineHeight: 2 }}>
            10.1 The Platform is provided on an <strong>"as is" and "as available"</strong> basis. We do not warrant that the Platform will be uninterrupted, error-free, or secure.<br />
            10.2 We do not warrant the accuracy, completeness, or reliability of any User-provided information, including Influencer metrics or Brand claims.
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="liability" title="11. Limitation of Liability">
          <Typography variant="body1" component="div" sx={{ lineHeight: 2 }}>
            11.1 To the maximum extent permitted under applicable law, the Company shall not be liable for any indirect, incidental, consequential, or punitive damages arising from use of the Platform, including loss of business, revenue, or goodwill.<br />
            11.2 The Company's aggregate liability for any claim arising out of these Terms shall not exceed the total platform fees paid by the User to the Company in the <strong>3 months</strong> preceding the claim.<br />
            11.3 Nothing in these Terms limits liability for fraud, gross negligence, or willful misconduct, or excludes any liability that cannot be excluded under Indian law.
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="indemnification" title="12. Indemnification">
          <Typography variant="body1" paragraph>
            Each User agrees to indemnify and hold harmless the Company, its officers, directors, and employees from any claims, losses, damages, or expenses (including legal fees) arising from:
          </Typography>
          <Typography variant="body1" component="ul" sx={{ pl: 2 }}>
            <li>Breach of these Terms</li>
            <li>Violation of applicable law or third-party rights</li>
            <li>Content submitted or claims made by the User</li>
            <li>Disputes between a Brand and an Influencer arising from a campaign</li>
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="termination" title="13. Termination">
          <Typography variant="body1" component="div" sx={{ lineHeight: 2 }}>
            13.1 We may suspend or terminate any User's account, with or without notice, for violation of these Terms, fraudulent activity, or as required by law.<br />
            13.2 Users may terminate their account at any time by written request, subject to settlement of pending payments/obligations.<br />
            13.3 Clauses relating to Indemnification, Limitation of Liability, IP, and Governing Law shall survive termination.
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="grievance" title="14. Grievance Redressal">
          <Typography variant="body1" paragraph>
            In compliance with the Information Technology (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021:
          </Typography>
          <Typography variant="body1" component="div" sx={{ lineHeight: 2 }}>
            <strong>Grievance Officer:</strong> Ramu Vemula<br />
            <strong>Email:</strong>{' '}<Link href="mailto:contact@infhire.com" color="primary">contact@infhire.com</Link><br />
            <strong>Address:</strong> 6-3-240/6/1, 1st Floor, Sarada Street, Road D-block, Maheswari Towers, Prem Nagar Road adj. Lane, Banjara Hills, Hyderabad, Telangana 500004, India<br />
            <strong>Acknowledgement:</strong> Within 24 hours<br />
            <strong>Resolution:</strong> Within 15 days of receipt of complaint
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="dispute-resolution" title="15. Dispute Resolution and Arbitration">
          <Typography variant="body1" component="div" sx={{ lineHeight: 2 }}>
            15.1 Any dispute arising out of or in connection with these Terms shall first be attempted to be resolved amicably through good-faith negotiation within <strong>30 days</strong>.<br />
            15.2 If unresolved, the dispute shall be referred to and finally resolved by arbitration under the <strong>Arbitration and Conciliation Act, 1996</strong>, by a sole arbitrator appointed mutually by the parties. The seat and venue of arbitration shall be <strong>Hyderabad</strong>, and the language of arbitration shall be English.<br />
            15.3 Subject to Clause 15.2, the courts at <strong>Hyderabad, Telangana</strong> shall have exclusive jurisdiction over matters not covered by arbitration (e.g., injunctive relief).
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="force-majeure" title="16. Force Majeure">
          <Typography variant="body1">
            Neither party shall be liable for any failure or delay in performance due to events beyond its reasonable control, including natural disasters, government action, internet/network outages, or pandemics.
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="amendment" title="17. Amendment">
          <Typography variant="body1">
            We reserve the right to modify these Terms at any time. Material changes will be notified via email or Platform notice. Continued use of the Platform after such changes constitutes acceptance of the amended Terms.
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="severability" title="18. Severability">
          <Typography variant="body1">
            If any provision of these Terms is held invalid or unenforceable, the remaining provisions shall continue in full force and effect.
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="entire-agreement" title="19. Entire Agreement">
          <Typography variant="body1">
            These Terms, along with the{' '}
            <Link component={RouterLink} to="/privacy" color="primary">Privacy Policy</Link>
            {' '}and any campaign-specific agreements, constitute the entire agreement between the parties and supersede all prior understandings.
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="governing-law" title="20. Governing Law">
          <Typography variant="body1">
            These Terms shall be governed by and construed in accordance with the laws of India.
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="contact" title="21. Contact Us">
          <Typography variant="body1" paragraph>For any questions regarding these Terms, please contact:</Typography>
          <Typography variant="body1" component="div" sx={{ lineHeight: 2 }}>
            <strong>Creativecrewz</strong><br />
            <strong>Email:</strong>{' '}<Link href="mailto:contact@infhire.com" color="primary">contact@infhire.com</Link><br />
            <strong>Address:</strong> 6-3-240/6/1, 1st Floor, Sarada Street, Road D-block, Maheswari Towers, Prem Nagar Road adj. Lane, Banjara Hills, Hyderabad, Telangana 500004, India
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        {/* Related policies */}
        <Box sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>Related Policies</Typography>
          <Link component={RouterLink} to="/privacy" color="primary" display="block" sx={{ mb: 0.5 }}>
            Privacy Policy
          </Link>
          <Link component={RouterLink} to="/refund-policy" color="primary" display="block">
            Refund and Cancellation Policy
          </Link>
        </Box>
      </Container>
    </>
  );
};

export default TermsAndConditions;
